// PROJECT 11* — V9 componentenbibliotheek: technische glyphs, generatieve pixels, geluiden.
// Dünne strokes, geel alleen voor actieve/belangrijke toestanden, reduced-motion bewust.
import { useEffect, useRef, useState, useCallback } from "react";
import { P11 } from "./p11shared";

// ---------- SVG glyph-bibliotheek ----------
export function TechnicalGlyph({ type = "crosshair", size = 18, color = "#858585", active = false, className = "", testid }) {
  const c = active ? P11.yellow : color;
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, className, "data-testid": testid || `p11-glyph-${type}` };
  const s = { stroke: c, strokeWidth: 1.2 };
  return (
    <svg {...common}>
      {type === "crosshair" && (<>
        <line x1="12" y1="2" x2="12" y2="8" {...s} /><line x1="12" y1="16" x2="12" y2="22" {...s} />
        <line x1="2" y1="12" x2="8" y2="12" {...s} /><line x1="16" y1="12" x2="22" y2="12" {...s} />
        <circle cx="12" cy="12" r="1.6" fill={c} />
      </>)}
      {type === "orbit" && (<>
        <circle cx="12" cy="12" r="9" {...s} opacity="0.6" />
        <ellipse cx="12" cy="12" rx="9" ry="3.6" {...s} opacity="0.45" />
        <circle cx="12" cy="12" r="2" fill={c} />
      </>)}
      {type === "square" && (<>
        <rect x="4" y="4" width="16" height="16" {...s} />
        <line x1="2" y1="4" x2="6" y2="4" {...s} /><line x1="18" y1="4" x2="22" y2="4" {...s} />
        <line x1="2" y1="20" x2="6" y2="20" {...s} /><line x1="18" y1="20" x2="22" y2="20" {...s} />
        <rect x="10.5" y="10.5" width="3" height="3" fill={c} />
      </>)}
      {type === "arrow" && (<>
        <line x1="3" y1="12" x2="19" y2="12" {...s} />
        <polyline points="13,6 19,12 13,18" {...s} />
      </>)}
      {type === "signal" && (<>
        <polyline points="2,12 6,12 8,6 11,18 14,8 16,12 22,12" {...s} />
      </>)}
      {type === "check" && (<>
        <rect x="3" y="3" width="18" height="18" {...s} />
        <polyline points="7,12.5 10.5,16 17,8" stroke={c} strokeWidth="1.6" fill="none" />
      </>)}
      {type === "nodes" && (<>
        <circle cx="5" cy="18" r="2" {...s} /><circle cx="12" cy="6" r="2" {...s} /><circle cx="19" cy="16" r="2" {...s} />
        <line x1="6.5" y1="16.5" x2="10.5" y2="7.5" {...s} opacity="0.6" /><line x1="13.8" y1="7" x2="17.5" y2="14.6" {...s} opacity="0.6" />
      </>)}
      {type === "marker" && (<>
        <circle cx="12" cy="10" r="6" {...s} />
        <line x1="12" y1="16" x2="12" y2="22" {...s} /><line x1="8" y1="22" x2="16" y2="22" {...s} />
        <circle cx="12" cy="10" r="1.5" fill={c} />
      </>)}
    </svg>
  );
}

// ---------- Generatief dot-matrix / pixel-veld ----------
// Langzaam morphende clusters: grid → symbool → lijnen → pixels. Reduced-motion: statisch.
export function PixelField({ size = 120, density = 9, className = "", testid = "p11-pixelfield" }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const cell = canvas.width / density;
    let raf, t = 0;

    // drie configuraties: volledige grid, kruis-symbool, horizontale lijnen
    const configs = [
      (x, y) => true,
      (x, y) => Math.abs(x - y) < 1 || Math.abs(x + y - (density - 1)) < 1 || x === Math.floor(density / 2) || y === Math.floor(density / 2),
      (x, y) => y % 3 === 0 && (x + y) % 2 === 0,
    ];
    const draw = () => {
      t += 0.004;
      const cycle = (t % 3);
      const idx = Math.floor(cycle);
      const blend = cycle - idx; // 0..1 binnen configuratie
      const cur = configs[idx];
      const next = configs[(idx + 1) % configs.length];
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let x = 0; x < density; x++) {
        for (let y = 0; y < density; y++) {
          const a = cur(x, y) ? 1 : 0;
          const b = next(x, y) ? 1 : 0;
          // geleidelijke overgang tussen configuraties
          const on = blend < 0.7 ? a : b;
          const jitter = reduced ? 0 : Math.sin(t * 6 + x * 1.7 + y * 2.3) * 0.25 + 0.75;
          const yellow = (x + y * density) % 17 === 0;
          ctx.fillStyle = yellow ? `rgba(255,228,77,${0.5 * jitter})` : `rgba(245,245,245,${(on ? 0.5 : 0.06) * jitter})`;
          const px = cell * 0.42;
          ctx.fillRect(x * cell + (cell - px) / 2, y * cell + (cell - px) / 2, px, px);
        }
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [size, density]);
  return <canvas ref={ref} style={{ width: size, height: size }} className={className} aria-hidden="true" data-testid={testid} />;
}

// ---------- Scanline-sweep voor overgangen ----------
export function ScanSweep({ trigger }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!trigger) return;
    setOn(true);
    const t = setTimeout(() => setOn(false), 650);
    return () => clearTimeout(t);
  }, [trigger]);
  if (!on) return null;
  return (
    <div className="fixed inset-0 z-40 pointer-events-none" aria-hidden="true" data-testid="p11-scansweep">
      <div style={{
        position: "absolute", left: 0, right: 0, height: 1,
        background: "rgba(255,228,77,0.5)",
        animation: "p11scan 0.6s cubic-bezier(0.7,0,0.2,1) both",
      }} />
    </div>
  );
}

// ---------- Geluiden (3 geüploade assets) ----------
export const P11_SOUNDS = {
  click: "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/882df061af49d1c1_samuelfjohanns-computer-processing-sound-effects-short-click-select-02-122133.mp3",
  processing: "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/b30b8b64a0d1ad9b_samuelfjohanns-computer-processing-sound-effect-01-122131.mp3",
  confirm: "https://customer-assets-gfyr7b9c.emergentagent.net/job_joh-boxing-preview/artifacts/7fddf5b92d3f868e_samuelfjohanns-computer-processing-sound-effects-short-click-select-01-122134.mp3",
};

const audioCache = {};

export function p11SoundEnabled() {
  return localStorage.getItem("p11_sound") !== "off";
}

export function useP11Sound() {
  const [enabled, setEnabled] = useState(p11SoundEnabled());
  const play = useCallback((kind) => {
    if (!p11SoundEnabled()) return;
    try {
      if (kind === "error") {
        // eigen foutgeluid: twee dalende synthetonen — bewust anders dan de upload-sounds
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = audioCache.errorCtx || (audioCache.errorCtx = new AC());
        const t0 = ctx.currentTime;
        [196, 138].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.0001, t0 + i * 0.16);
          gain.gain.exponentialRampToValueAtTime(0.12, t0 + i * 0.16 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.16 + 0.15);
          osc.connect(gain).connect(ctx.destination);
          osc.start(t0 + i * 0.16);
          osc.stop(t0 + i * 0.16 + 0.16);
        });
        return;
      }
      if (!audioCache[kind]) {
        audioCache[kind] = new Audio(P11_SOUNDS[kind]);
        audioCache[kind].volume = kind === "processing" ? 0.3 : 0.25;
      }
      const a = audioCache[kind];
      a.currentTime = 0;
      a.play().catch(() => {}); // browser kan playback blokkeren — stilletjes doorgaan
    } catch { /* audio nooit blokkerend */ }
  }, []);
  const toggle = useCallback(() => {
    const next = !p11SoundEnabled();
    localStorage.setItem("p11_sound", next ? "on" : "off");
    setEnabled(next);
  }, []);
  return { play, enabled, toggle };
}

export function SoundToggle({ className = "" }) {
  const { enabled, toggle, play } = useP11Sound();
  return (
    <button onClick={() => { toggle(); }} className={`p11-label hover:text-white transition-colors ${className}`} data-testid="p11-sound-toggle" aria-pressed={enabled}>
      {enabled ? "SOUND ON" : "SOUND OFF"}
    </button>
  );
}
